// react
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// material ui
import { Box, Divider, IconButton, Typography } from '@mui/material';

// shared components
import { ConfirmationDialog } from '@/components/ui/dialog/ConfirmationDialog';

// components
// forms
import EditMeterDisplayNameForm from '@/features/energyxplorer/portfolio/forms/EditMeterDisplayNameForm';
import EditMeterOccupancyForm from '@/features/energyxplorer/portfolio/forms/EditMeterOccupancyForm';
import EditMeterUtilityProviderForm from '@/features/energyxplorer/portfolio/forms/EditMeterUtilityProviderForm';
// types
import type { AnalysisType } from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

interface MeterEditPanelProps {
  meter: Record<string, unknown>;
  analysisType: AnalysisType;
  showOccupancy: boolean;
  onBack: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

function getMeterId(
  meter: Record<string, unknown>,
  analysisType: AnalysisType,
): string | undefined {
  if (analysisType === 'power') {
    const id = meter.metering_point_id ?? meter.MeteringPointId;
    return id ? String(id) : undefined;
  }
  const ds = meter.data_stream as Record<string, unknown> | undefined;
  const id = ds?.unit_id ?? meter.UnitId;
  return id ? String(id) : undefined;
}

export default function MeterEditPanel({
  meter,
  analysisType,
  showOccupancy,
  onBack,
  onDirtyChange,
}: MeterEditPanelProps) {
  const { t } = useTranslation('energyxplorer');
  const account = useSelector(getSelectedAccount);
  console.log('MeterEditPanel meter:', meter);
  const accountId = account?.AccountId;
  const meterId = getMeterId(meter, analysisType);

  const [displayNameDirty, setDisplayNameDirty] = useState(false);
  const [occupancyDirty, setOccupancyDirty] = useState(false);
  const [utilityProviderDirty, setUtilityProviderDirty] = useState(false);
  const [confirmBack, setConfirmBack] = useState(false);

  const anyDirty = displayNameDirty || occupancyDirty || utilityProviderDirty;

  useEffect(() => {
    onDirtyChange?.(anyDirty);
  }, [anyDirty, onDirtyChange]);

  const handleBack = () => {
    if (anyDirty) {
      setConfirmBack(true);
    } else {
      onBack();
    }
  };

  if (!accountId || !meterId) return null;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={handleBack} size="small" aria-label="back">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('matrikel.edit_meter', { defaultValue: 'Edit meter' })}
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={2}>
        <EditMeterDisplayNameForm
          accountId={accountId}
          meterId={meterId}
          analysisType={analysisType}
          initialDisplayName={
            (meter.DisplayName ??
              meter.display_name ??
              meter.Name ??
              meter.address) as string | null | undefined
          }
          onDirtyChange={setDisplayNameDirty}
        />

        {!showOccupancy && (
          <>
            <Divider />
            <EditMeterOccupancyForm
              accountId={accountId}
              meterId={meterId}
              analysisType={analysisType}
              onDirtyChange={setOccupancyDirty}
            />
          </>
        )}

        {analysisType !== 'power' && (
          <>
            <Divider />
            <EditMeterUtilityProviderForm
              accountId={accountId}
              meterId={meterId}
              analysisType={analysisType}
              onDirtyChange={setUtilityProviderDirty}
            />
          </>
        )}
      </Box>

      <ConfirmationDialog
        open={confirmBack}
        variant="confirm"
        message={t('matrikel.unsaved_changes_confirm', {
          defaultValue:
            'You have unsaved changes. Are you sure you want to go back?',
        })}
        onConfirm={() => {
          setConfirmBack(false);
          onBack();
        }}
        onClose={() => setConfirmBack(false)}
      />
    </Box>
  );
}
